/* tslint:disable:max-line-length */

import Constants from "../../Action/Constants";
import { ActionInterface } from "../../Interface/ActionInterface";
import { PersonInterface } from "../../Entity/Entities/Person/PersonInterface";
import { CompanyInterface } from "../../Entity/Entities/Company/CompanyInterface";
import { ContractInterface } from "../../Entity/Entities/Contract/ContractInterface";
import { ConversationInterface } from "../../Entity/Entities/Conversation/ConversationInterface";
import { ChargeTemplateInterface } from "../../Entity/Entities/Charge/ChargeTemplateInterface";
import { AgencyFeeInterface } from "../../Entity/Entities/AgencyFee/AgencyFeeInterface";
import { EmailAccountInterface } from "../../Entity/Entities/EmailAccount/EmailAccountInterface";
import { UserSettingsInterface } from "../../Entity/Entities/Settings/User/UserSettingsInterface";
import { AgencySettingsInterface } from "../../Entity/Entities/Settings/Agency/AgencySettingsInterface";
import { SubscriptionInterface } from "../../Entity/Entities/Subscription/SubscriptionInterface";
import { TransactionFileInterface } from "../../Entity/Entities/TransactionFile/TransactionFileInterface";
import { ActivityTemplateInterface } from "../../Entity/Entities/Activity/ActivityTemplateInterface";
import { TaskTemplateInterface } from "../../Entity/Entities/Task/TaskTemplateInterface";
import { BankAccountInterface } from "../../Entity/Entities/BankAccount/BankAccountInterface";
import { PaymentInterface } from "../../Entity/Entities/Payment/PaymentInterface";
import LocaleFactory from "../../Entity/Factories/LocaleFactory";
import LanguageFactory from "../../Entity/Factories/LanguageFactory";
import LanguageContainer from "../../Translation/LanguageContainer";
import CurrencyContainer from "../../Currency/CurrencyContainer";
import Translator from "../../Translation/Translator";
import * as uuidV4 from "uuid/v4";

interface RelationInterface {
  [key: number]: number[];
}

interface SubscriptionRelationInterface {
  [key: number]: SubscriptionInterface[];
}

interface PaginatorInterface {
  [key: string]: number;
}

interface StateInterface {
  loadedEntities: string[];
  session: {
    data: {},
    user: PersonInterface | any
  };
  settings: {
    user: UserSettingsInterface,
    agency: AgencySettingsInterface
  };
  propertiesInCompanies: RelationInterface;
  personsInCompanies: RelationInterface;
  contractsInUnits: RelationInterface;
  contractsInPersons: RelationInterface;
  taskTemplatesInActivityTemplates: RelationInterface;
  chargeTemplatesInContracts: RelationInterface;
  chargeTemplatesInUnits: RelationInterface;
  chargeTemplatesInProperties: RelationInterface;
  persons: PersonInterface[];
  companies: CompanyInterface[];
  contracts: ContractInterface[];
  chargeTemplates: ChargeTemplateInterface[];
  agencyFees: AgencyFeeInterface[];
  emailAccounts: EmailAccountInterface[];
  subscriptions: SubscriptionRelationInterface;
  transactionFiles: TransactionFileInterface[];
  snackbars: any[];
  socketDisconnected: boolean;
  isUploadingSharedAttachment: boolean;
  isUploadingPrivateAttachment: boolean;
  redirectPathAfterAuth: string;
  paginator: PaginatorInterface;
  personIdForCreatingContract: number;
  savedDataBeforeRedirecting: object;
  chargeTypes: any[];
  redirectPath: string;
  postProcessMessage: string;
  activityTemplates: ActivityTemplateInterface[];
  taskTemplates: TaskTemplateInterface[];
  contractUpdatePageTabIndex: number;
  unitUpdatePageTabIndex: number;
  propertyUpdatePageTabIndex: number;
  companyUpdatePageTabIndex: string;
  contractStatuses: {};
  bankAccounts: BankAccountInterface[];
  globalTypes: {};
  templateForPersonCreatePage: string;
  templateForCompanyCreatePage: string;
  breadcrumbs: string[];
  lookupSearchText: string;
  unitsPageTabIndex: number;
  financeUpdatePageTabIndex: number;
  contactUpdatePageTabIndex: number;
  settingsUpdatePageTabIndex: number;
  dashboardUpdatePageTabIndex: number;
  financePaymentsFilterBankAccountState: string;
  financePaymentsFilterState: string;
  financePaymentsFilterDateState: any;
  financePaymentsFilterSearchState: string;
  savingsPaymentsFilterSearchState: {};
  financeBankTransactionsFilterState: string;
  financeChargesFilterState: string;
  financeChargesFilterDateState: any;
  financeChargesFilterSearchState: string;
  tasksFilterSearchState: string;
  notesFilterState: string;
  notesFilterSearchState: string;
  notificationsFilterState: string;
  notificationsFilterSearchState: string;
  contractsFilterState: string;
  contractsFilterSearchState: string;
  contactsFilterState: string;
  contactsFilterSearchState: string;
  companiesFilterState: string;
  companiesFilterSearchState: string;
  activitiesFilterState: string;
  activitiesFilterSearchState: string;
  defaultPaginationAmount: number;
  smallAmountForCharge: number;
  showTransactionPaymentsList: boolean;
  postProcessSignUpPopupOpenMessage: string;
  selectedUnitId: any;
  isBusyPopupOpen: boolean;
  fetchedStatementDatas: any[];
  tasksFilterCheckBoxState: any;
  financeChargesFilterCheckBoxState: any;
  dashboardPayablesFiltersState: any;
  dashboardReceivablesFiltersState: any;
  financeChargesListPosition: number;
  dashboardChargesListPosition: number;
  financePaymentsListPosition: number;
  dashboardPaymentsListPosition: number;
  savingsPaymentsListPosition: number;
  financeBankTransactionsListPosition: number;
  chargesPaymentsLinkedLimitNumber: number;
  transactionsFilterStates: {};
  listSearchTimeout: number;
  selectedPropertyId: number;
  chargeDetailsAction: string;
  selectedСompanyId: number;
  normalSnackbarDelay: number;
  persistentSnackbarDelay: number;
  statementsFilterCheckBoxState: any;
  launchPanelHide: boolean;
  dashboardReceivableChargesCount: number;
  dashboardPayableChargesCount: number;
  financeOwnerStatementsFilterSearchState: string;
  savingsFilterSearchState: string;
  dashboardPaymentsCount: number;
  dashboardPaymentsFilterCheckBoxState: any;
  dashboardStatementsCount: number;
  dashboardTasksCount: number;
  conversations: ConversationInterface[];
  dashboardStatementsListPosition: number;
  financeStatementsListPosition: number;
  nextTransactionPayments: PaymentInterface[];
  settingsUpdateAgencyCategory: string;
  transactionPaymentIndex: number;
  isLanguageKeySwitchOn: any;
  pontoMessageShown: boolean;
  pontoSynchronizeButtonDisabled: boolean;
  clientAccount: boolean;
}

const initialState: StateInterface = {
  loadedEntities: [],
  session: {
    data: {},
    user: {}
  },
  settings: {
    user: {
      language: LanguageContainer.resolve("nl-NL"),
      currency: CurrencyContainer.pickFromId(1),
      locale: LocaleFactory.create({ iso: "nl-NL" }),
      beta: false,
      launchPanelState: true,
      entityPanelState: true,
      finishWelcomeCoach: false
    },
    agency: {
      defaultLocale: LocaleFactory.create({ iso: "nl-NL" }),
      defaultLanguage: LanguageFactory.create({ iso: "nl-NL" }),
      defaultCalculation: "",
      financialStartDate: null,
      notificationPeriod: 2,
      isBusy: false,
      vatPercentage: 21,
      isActivitiesShown: false,
      isActivateInvoiceNumber: false,
      reviewOwnerStatements: false,
      statementEmailTextEn: "",
      statementEmailTextNl: "",
      adjustRentEmailTextEN: "",
      adjustRentEmailTextNL: "",
      useAgencyNameForEmail: false,
      sendCopyToEmails: [],
      sendCopyToSubject: "",
      useAgencyAsSenderForAutoEmails: true,
    }
  },
  persons: [],
  companies: [],
  contracts: [],
  chargeTemplates: [],
  agencyFees: [],
  emailAccounts: [],
  personsInCompanies: {},
  propertiesInCompanies: {},
  contractsInUnits: {},
  contractsInPersons: {},
  chargeTemplatesInContracts: {},
  chargeTemplatesInUnits: {},
  chargeTemplatesInProperties: {},
  subscriptions: {},
  transactionFiles: [],
  snackbars: [],
  socketDisconnected: false,
  isUploadingSharedAttachment: false,
  isUploadingPrivateAttachment: false,
  redirectPathAfterAuth: "",
  paginator: {},
  personIdForCreatingContract: 0,
  savedDataBeforeRedirecting: {},
  chargeTypes: [],
  redirectPath: "",
  postProcessMessage: "",
  activityTemplates: [],
  taskTemplates: [],
  taskTemplatesInActivityTemplates: {},
  contractUpdatePageTabIndex: 0,
  unitUpdatePageTabIndex: 0,
  financeUpdatePageTabIndex: 0,
  propertyUpdatePageTabIndex: 0,
  companyUpdatePageTabIndex: Constants.COMPANY_DETAILS_OVERVIEW_TAB,
  contractStatuses: {},
  bankAccounts: [],
  globalTypes: {},
  templateForPersonCreatePage: "",
  templateForCompanyCreatePage: "",
  breadcrumbs: [],
  lookupSearchText: "",
  unitsPageTabIndex: 0,
  contactUpdatePageTabIndex: 0,
  settingsUpdatePageTabIndex: 0,
  dashboardUpdatePageTabIndex: 0,
  financePaymentsFilterBankAccountState: "",
  financePaymentsFilterState: "unused",
  financePaymentsFilterDateState: null,
  financePaymentsFilterSearchState: "",
  savingsPaymentsFilterSearchState: {},
  financeBankTransactionsFilterState: "unused",
  financeChargesFilterState: "all",
  financeChargesFilterDateState: null,
  financeChargesFilterSearchState: "",
  tasksFilterSearchState: "",
  notesFilterState: "all",
  notesFilterSearchState: "",
  notificationsFilterState: "open",
  notificationsFilterSearchState: "",
  contractsFilterState: "conceptExpiringEnding",
  contractsFilterSearchState: "",
  activitiesFilterState: "open",
  activitiesFilterSearchState: "",
  defaultPaginationAmount: 30,
  smallAmountForCharge: 100,
  showTransactionPaymentsList: true,
  postProcessSignUpPopupOpenMessage: "",
  selectedUnitId: null,
  selectedPropertyId: null,
  selectedСompanyId: null,
  isBusyPopupOpen: false,
  fetchedStatementDatas: [],
  tasksFilterCheckBoxState: {},
  financeChargesFilterCheckBoxState: {},
  dashboardPayablesFiltersState: {},
  dashboardReceivablesFiltersState: {},
  financeChargesListPosition: 0,
  dashboardChargesListPosition: 0,
  financePaymentsListPosition: 0,
  dashboardPaymentsListPosition: 0,
  savingsPaymentsListPosition: 0,
  financeBankTransactionsListPosition: 0,
  chargesPaymentsLinkedLimitNumber: 6,
  transactionsFilterStates: {},
  listSearchTimeout: 700,
  chargeDetailsAction: "",
  normalSnackbarDelay: 2000,
  persistentSnackbarDelay: 30000,
  statementsFilterCheckBoxState: {},
  launchPanelHide: false,
  dashboardReceivableChargesCount: 0,
  dashboardPayableChargesCount: 0,
  financeOwnerStatementsFilterSearchState: "",
  savingsFilterSearchState: "",
  dashboardPaymentsCount: 0,
  dashboardPaymentsFilterCheckBoxState: {},
  dashboardStatementsCount: 0,
  dashboardTasksCount: 0,
  conversations: [],
  dashboardStatementsListPosition: 0,
  financeStatementsListPosition: 0,
  nextTransactionPayments: [],
  settingsUpdateAgencyCategory: "",
  transactionPaymentIndex: 0,
  pontoMessageShown: false,
  pontoSynchronizeButtonDisabled: false,
  isLanguageKeySwitchOn: JSON.parse(sessionStorage.getItem("keySwitch")),
  contactsFilterState: "",
  contactsFilterSearchState: "",
  companiesFilterState: "",
  companiesFilterSearchState: "",
  clientAccount: false
};

export const appReducer = (state: StateInterface = initialState, action: ActionInterface): StateInterface => {
  switch (action.type) {
    case Constants.PAGE_BANK_TRANSACTIONS_LIST_OF_NEXT_PAYMENTS_UPDATE:
      return {
        ...state,
        nextTransactionPayments: [...action.payload?.payments],
        transactionPaymentIndex: action.payload?.index
      };

    case Constants.SWITCH_TRANSLATION_KEYS:
      return {
        ...state,
        isLanguageKeySwitchOn: action.payload?.languageKeySwitch
      };

    case Constants.CHARGE_DETAILS_UPDATE_ACTION:
      return {
        ...state,
        chargeDetailsAction: action.payload?.action
      }

    case Constants.BANK_TRANSACTION_SHOW_PAYMENTS_LIST:
      return {
        ...state,
        showTransactionPaymentsList: action.payload?.showTransactionPaymentsList
      };

    case Constants.GLOBAL_IS_BUSY_POPUP_OPEN:
      return {
        ...state,
        isBusyPopupOpen: true
      };

    case Constants.GLOBAL_IS_BUSY_POPUP_CLOSE:
      return {
        ...state,
        isBusyPopupOpen: false
      };

    case Constants.GLOBAL_GET_AGENCY_LIST:
      return {
        ...state,
      };

    /*
    |  Reminders
    */
    case Constants.GLOBAL_REMINDERS_SAVE_LIST_SUCCESS:
      {
        return {
          ...state,
          loadedEntities: [...new Set([action.type, ...state.loadedEntities])]

        };
      }

    /*
    |  Counts
    */
    case Constants.GLOBAL_NOTIFICATIONS_COUNT_SUCCESS:
      {
        return {
          ...state,
          loadedEntities: [...new Set([action.type, ...state.loadedEntities])]

        };
      }

    case Constants.GLOBAL_ACTIVITIES_COUNT_SUCCESS:
      {
        return {
          ...state,
          loadedEntities: [...new Set([action.type, ...state.loadedEntities])]

        };
      }

    case Constants.GLOBAL_UNFINISHED_TASKS_COUNT_SUCCESS:
      {
        return {
          ...state,
          loadedEntities: [...new Set([action.type, ...state.loadedEntities])]

        };
      }

    /*
    |  Global Types
    */
    case Constants.GLOBAL_RECEIVE_DATA_TYPES_LIST_SUCCESS:
    {
      return {
        ...state,
        loadedEntities: [...new Set([action.type, ...state.loadedEntities])]
      };
    }

    /*
    |  Lookup
    */

    case Constants.LOOKUP_SEARCHTEXT:
      return {
        ...state,
        lookupSearchText: action.payload?.lookupSearchText
      };

    case Constants.LOOKUP_SEARCHTEXT_CLEAR:
      return {
        ...state,
        lookupSearchText: ""
      };

    /*
    |  Statuses
    */
    case Constants.GLOBAL_CONTRACT_STATUSES_LIST_SUCCESS:
    return {
      ...state,
      contractStatuses: action.payload?.contractStatuses,
      loadedEntities: [action.type, ...state.loadedEntities]
    };

    /*
    |  STATEMENT
    */

    case Constants.GLOBAL_FINANCIAL_STATEMENT_DATA_SUCCESS:
      return {
        ...state,
        fetchedStatementDatas: [
          ...action.payload?.financialStatements,
          ...state.fetchedStatementDatas.filter(statementData => !action.payload?.statementIds.includes(statementData.id))
        ]
      };


    /*
    |  Dashboard
    */

    case Constants.PAGE_TASKS_LIST_CHECK_FILTER:
      return {
        ...state,
        tasksFilterCheckBoxState: {...state.tasksFilterCheckBoxState, ...action.payload},
      };

    case Constants.FINANCE_CHARGES_LIST_CHECK_FILTER:
      return {
        ...state,
        financeChargesFilterCheckBoxState: {...state.financeChargesFilterCheckBoxState, ...action.payload},
      };

    case Constants.DASHBOARD_PAYABLES_UPDATE_FILTERS_STATE:
      return {
        ...state,
        dashboardPayablesFiltersState: {
          ...state.dashboardPayablesFiltersState,
          ...action.payload,
        }
      };

    case Constants.DASHBOARD_RECEIVABLES_UPDATE_FILTERS_STATE:
      return {
        ...state,
        dashboardReceivablesFiltersState: {
          ...state.dashboardReceivablesFiltersState,
          ...action.payload
        },
      };

    case Constants.DASHBOARD_PAYMENTS_LIST_CHECK_FILTER:
        return {
          ...state,
          dashboardPaymentsFilterCheckBoxState: {...state.dashboardPaymentsFilterCheckBoxState, ...action.payload},
        };

    case Constants.PAGE_STATEMENT_LIST_CHECK_FILTER:
      return {
        ...state,
        statementsFilterCheckBoxState: {...state.statementsFilterCheckBoxState, ...action.payload},
      };

    /*
    |  Statements
    */

    case Constants.FINANCE_OWNER_STATEMENTS_FILTER_SEARCH_UPDATE_STATE:
      return {
        ...state,
        financeOwnerStatementsFilterSearchState: action.payload?.filter
      };

    /*
    |  Savings
    */

    case Constants.SAVINGS_FILTER_SEARCH_UPDATE_STATE:
      return {
        ...state,
        savingsFilterSearchState: action.payload?.filter
      };

    /*
    |  Tabs
    */

    case Constants.PAGE_CONTRACT_UPDATE_SWITCH_TAB:
      return {
        ...state,
        contractUpdatePageTabIndex: action.payload?.index
      };

    case Constants.PAGE_UNIT_UPDATE_SWITCH_TAB:
      return {
        ...state,
        unitUpdatePageTabIndex: action.payload?.index,
        selectedUnitId: action.payload?.unitId
      };

    case Constants.PAGE_FINANCE_UPDATE_SWITCH_TAB:
      return {
        ...state,
        financeUpdatePageTabIndex: action.payload?.index
      };

    case Constants.PAGE_UNITS_SWITCH_TAB:
      return {
        ...state,
        unitsPageTabIndex: action.payload?.index
      };


    case Constants.PAGE_CONTACTS_SWITCH_TAB:
      return {
        ...state,
        contactUpdatePageTabIndex: action.payload?.index
      };

    case Constants.PAGE_DASHBOARD_UPDATE_SWITCH_TAB:
      return {
        ...state,
        dashboardUpdatePageTabIndex: action.payload?.index
      };

    case Constants.GLOBAL_SAVE_UNPAID_RECEIVABLE_CHARGES_COUNT:
      return {
        ...state,
        dashboardReceivableChargesCount: action.payload?.dashboardReceivableChargesCount
      };

    case Constants.GLOBAL_SAVE_UNPAID_PAYABLE_CHARGES_COUNT:
      return {
        ...state,
        dashboardPayableChargesCount: action.payload?.dashboardPayableChargesCount
      };

    case Constants.GLOBAL_SAVE_UNUSED_PAYMENTS_COUNT:
      return {
        ...state,
        dashboardPaymentsCount: action.payload?.dashboardPaymentsCount
      };

    case Constants.GLOBAL_SAVE_NOTSENT_STATEMENTS_COUNT:
      return {
        ...state,
        dashboardStatementsCount: action.payload?.dashboardStatementsCount
      };

    case Constants.GLOBAL_SAVE_NOTFINISHED_TASKS_COUNT:
      return {
        ...state,
        dashboardTasksCount: action.payload?.dashboardTasksCount
      };

    case Constants.PAGE_SETTINGS_UPDATE_SWITCH_TAB:
      return {
        ...state,
        settingsUpdatePageTabIndex: action.payload?.index
      };

    case Constants.PAGE_SETTINGS_UPDATE_AGENCY_CATEGORY:
      return {
        ...state,
        settingsUpdateAgencyCategory: action.payload?.category
      };

    case Constants.PAGE_PROPERTY_UPDATE_SWITCH_TAB:
      return {
        ...state,
        propertyUpdatePageTabIndex: action.payload?.index,
        selectedPropertyId: action.payload?.propertyId
      };

    case Constants.PAGE_CONTACT_COMPANY_UPDATE_SWITCH_TAB:
      return {
        ...state,
        companyUpdatePageTabIndex: action.payload?.index,
        selectedСompanyId: action.payload?.companyId
      };


    /*
    |  User
    */

    case Constants.AUTH_SIGNIN:
      return {
        ...state,
        session: {
          data: state.session.data,
          user: action.payload?.user
        },
        settings: {
          user: action.payload?.settings,
          agency: state.settings.agency
        }
      };

    case Constants.AUTH_SIGNOUT:
      return {
        ...initialState,
        redirectPathAfterAuth: action.payload?.shouldSaveRedirectorPath ? state.redirectPathAfterAuth : ""
      };

    /*
    |  Settings
    */

    case Constants.PAGE_SETTINGS_PREFERENCES_UPDATE_SUCCESS:
      return {
        ...state,
        settings: {
          user: action.payload?.settings,
          agency: state.settings.agency
        }
      };

    case Constants.PAGE_SETTINGS_AGENCY_PROFILE_UPDATE_SETTINGS_SUCCESS:
      return {
        ...state,
        settings: {
          user: state.settings.user,
          agency: action.payload?.settings,
        }
      };

    /*
    |  Snackbar
    */

    case Constants.SNACKBAR_ADD_MESSAGE:
      const snackbars = [...state.snackbars];

      if (action.payload?.message && action.payload?.type) {
        if (snackbars.some(snackbar => snackbar?.message?.toLowerCase() === action.payload?.message?.toLowerCase())) {
          return {
            ...state,
            snackbars: [...snackbars]
          };
        }

        const snackbar: any = {
          message: action.payload?.message,
          type: action.payload?.type
        }

        snackbar.id = uuidV4();

        snackbars.push(snackbar);
      }

      return {
        ...state,
        snackbars: [...snackbars]
      };

    case Constants.SNACKBAR_REMOVE_MESSAGE:
      return {
        ...state,
        snackbars: [...state.snackbars].filter(snackbar => snackbar?.id != action.payload?.id)
      };

    case Constants.SNACKBAR_CLEAR_MESSAGES:
      return {
        ...state,
        snackbars: []
      };

    /*
    |  Redirector
    */

    case Constants.REDIRECTOR_ADD_PATHNAME:
      return {
        ...state,
        redirectPathAfterAuth: action.payload?.pathname
      };

    /*
    |  Agency
    */

    case Constants.GLOBAL_AGENCY_LIST_SUCCESS:
      return {
        ...state,
        settings: {
          user: state.settings.user,
          agency: action.payload?.settings
        },
        loadedEntities: [action.type, ...state.loadedEntities]
      };

      case Constants.GLOBAL_GET_AGENCY_LIST_SUCCESS:
        return {
          ...state,
          settings: {
            user: state.settings.user,
            agency: action.payload?.settings
          },
        };


    /*
    |  Person
    */

    case Constants.GLOBAL_PERSON_LIST_SUCCESS:
      return {
        ...state,
        persons: action.payload?.persons,
        loadedEntities: [action.type, ...state.loadedEntities]
      };

    case Constants.PAGE_CONTACT_PERSON_CREATE_SUCCESS:
    case Constants.LOOKUP_COMPONENT_PERSON_CREATE_SUCCESS:
    {
      const personsInCompanies = {};
      action.payload?.person?.companies?.forEach(company => {
        return personsInCompanies[company?.id] = [
          ...new Set([
            ...[action.payload?.person?.id],
            ...state.personsInCompanies[company?.id] || []
          ])
        ];
      })

      return {
        ...state,
        persons: state.persons.some(person => person.id === action.payload?.person?.id) ?
          state.persons : [action.payload?.person, ...state.persons],
        personsInCompanies: {
          ...state.personsInCompanies,
          ...personsInCompanies
        }
      };
    }

    case Constants.PAGE_CONTACT_PERSON_UPDATE_SUCCESS:
    case Constants.PAGE_SETTINGS_USER_PROFILE_UPDATE_SUCCESS:
    {
      const personsInCompanies = {...state.personsInCompanies};

      Object.keys(personsInCompanies || {}).forEach(company => {
        if (personsInCompanies[company].includes(action.payload?.person?.id) && action.payload?.person?.companies?.length && action.payload?.person?.companies?.some(cmpn => cmpn?.id === company)) {
          personsInCompanies[company] = (personsInCompanies[company] || []).filter(
            personId => personId !== action.payload?.person?.id
          )
        }
      })

      const newPersonInCompanies = {};

      action.payload?.person?.companies?.forEach(company => {
        return newPersonInCompanies[company?.id] = [
          ...new Set([
            ...[action.payload?.person?.id],
            ...personsInCompanies[company?.id] || []
          ])
        ];
      })

      return {
        ...state,
        persons: [
          action.payload?.person,
          ...state.persons.filter(person => person.id !== action.payload?.person?.id)
        ],
        personsInCompanies: {
          ...personsInCompanies,
          ...newPersonInCompanies
        },
        session: {
          ...state.session,
          user: action.payload?.person?.id === state.session.user.id ? action.payload?.person : state.session.user
        }
      };
    }

    case Constants.GLOBAL_PERSON_DELETE_REQUEST:
      return {
        ...state
      };

    case Constants.GLOBAL_PERSON_DELETE_SUCCESS:
      return {
        ...state,
        persons: [
          action.payload?.person,
          ...state.persons.filter(person => person.id !== action.payload?.person.id)
        ]
      };

    /*
    |  Company
    */

    case Constants.GLOBAL_COMPANY_LIST_SUCCESS:
      const personsInCompanies = {};
      const propertiesInCompanies = {};

      action.payload?.companies?.forEach(company => {
        if (!company.personIds) {
          personsInCompanies[company.id] = [];
          return;
        }

        personsInCompanies[company.id] = [...company.personIds];
      });

      action.payload?.companies?.forEach(company => {
        if (!company.propertyIds) {
          propertiesInCompanies[company.id] = [];
          return;
        }

        propertiesInCompanies[company.id] = [...company.propertyIds];
      });

      return {
        ...state,
        companies: action.payload?.companies,
        personsInCompanies: personsInCompanies,
        propertiesInCompanies: propertiesInCompanies,
        loadedEntities: [action.type, ...state.loadedEntities]
      };

    case Constants.PAGE_CONTACT_COMPANY_CREATE_SUCCESS:
    case Constants.LOOKUP_COMPONENT_COMPANY_CREATE_SUCCESS:
      return {
        ...state,
        companies: state.companies.some(company => company.id === action.payload?.company?.id) ?
          state.companies : [action.payload?.company, ...state.companies],
        personsInCompanies: {
          ...state.personsInCompanies,
        },
        propertiesInCompanies: {
          ...state.propertiesInCompanies,
          [action.payload?.company?.id]: []
        }
      };

    case Constants.PAGE_CONTACT_COMPANY_UPDATE_SUCCESS:
      return {
        ...state,
        companies: [
          action.payload?.company,
          ...state.companies.filter(company => company.id !== action.payload?.company?.id),
        ]
      };

    case Constants.GLOBAL_COMPANY_DELETE_REQUEST:
      return {
        ...state
      };

    case Constants.GLOBAL_COMPANY_DELETE_SUCCESS:
      return {
        ...state,
        companies: [
          action.payload?.company,
          ...state.companies.filter(company => company.id !== action.payload?.company?.id)
        ]
      };

    case Constants.GLOBAL_COMPANY_ADD_PERSON:
      return {
        ...state,
        personsInCompanies: {
          ...state.personsInCompanies,
          [action.payload?.id]: [
            ...new Set([
              ...[action.payload?.person?.id],
              ...state.personsInCompanies[action.payload?.id] || []
            ])
          ]
        }
      };

    case Constants.GLOBAL_COMPANY_UNLINK_PERSON_SUCCESS:
      return {
        ...state,
        personsInCompanies: {
          ...state.personsInCompanies,
          [action.payload?.companyId]: state.personsInCompanies[action.payload?.companyId]
          .filter(personId => {
            return personId !== action.payload?.person?.id;
          })
        },
        persons: [
          action.payload?.person,
          ...state.persons.filter(person => person.id !== action.payload?.person?.id)
        ]
      };

    /*
    |  Conversations
    */

    case Constants.GLOBAL_CONVERSATIONS_SAVE_LIST_SUCCESS:
      return {
        ...state,
        conversations: action.payload?.conversations?.sort((conversation1, conversation2) => conversation1?.id - conversation2?.id),
        loadedEntities: [action.type, ...state.loadedEntities]
      };

    case Constants.GLOBAL_CONVERSATION_CREATE_SUCCESS:
      return {
        ...state,
        conversations: state.conversations.some(conversation => conversation?.id === action.payload?.conversation?.id) ?
          state.conversations?.sort((conversation1, conversation2) => conversation1?.id - conversation2?.id) :
          [action.payload?.conversation, ...state.conversations].sort((conversation1, conversation2) => conversation1?.id - conversation2?.id)
      };

    case Constants.GLOBAL_CONVERSATION_UPDATE_SUCCESS:
    case Constants.GLOBAL_ATTACHMENT_CONVERSATION_UPLOAD_SUCCESS:
    case Constants.GLOBAL_ATTACHMENT_CONVERSATION_DELETE_SUCCESS:
      return {
        ...state,
        conversations: [
          action.payload?.conversation,
          ...state.conversations.filter(conversation => conversation?.id !== action.payload?.conversation?.id),
        ].sort((conversation1, conversation2) => conversation1?.id - conversation2?.id)
      };

    /*
    |  Property
    */

    case Constants.GLOBAL_PROPERTY_LIST_SUCCESS:
      return {
        ...state,
        loadedEntities: [action.type, ...state.loadedEntities]
      };

    case Constants.PAGE_PROPERTY_CREATE_SUCCESS:
      return {
        ...state,
        propertiesInCompanies: action.payload?.property?.company ? {
          ...state.propertiesInCompanies,
          [action.payload?.property?.company?.id]: [
            ...new Set([
              ...[action.payload?.property?.id],
              ...state.propertiesInCompanies[action.payload?.property?.company?.id] || []
            ])
          ]
        } : state.propertiesInCompanies
      };

    case Constants.PAGE_PROPERTY_UPDATE_SUCCESS: {
      const propertiesInCompanies = {...state.propertiesInCompanies};
      let ownerWasChanged = false;

      Object.keys(propertiesInCompanies || {}).forEach(company => {
        if (propertiesInCompanies[company].includes(action.payload?.property?.id) && company !== action.payload?.property?.companyId) {
          ownerWasChanged = true;

          propertiesInCompanies[company] = (propertiesInCompanies[company] || []).filter(
            propertyId => propertyId !== action.payload?.property?.id
          )
        }
      });

      if (ownerWasChanged) {
        propertiesInCompanies[action.payload?.property?.companyId] = [
          ...new Set([
            ...[action.payload?.property?.id],
            ...state.propertiesInCompanies[action.payload?.property?.companyId] || []
          ])
        ];
      }

      return {
        ...state,
        propertiesInCompanies: {...propertiesInCompanies}
      };
    }

    /*
    |  Unit
    */

    case Constants.GLOBAL_UNIT_LIST_SUCCESS:
      const contractsInUnits = {};
      action.payload?.units?.forEach(unit => {
        if (!unit.contractIds) {
          contractsInUnits[unit.id] = [];
          return;
        }

        contractsInUnits[unit.id] = [...unit.contractIds];
      });

      return {
        ...state,
        contractsInUnits: contractsInUnits,
        loadedEntities: [action.type, ...state.loadedEntities]
      };


    /*
    |  Contracts
    */

    case Constants.GLOBAL_CONTRACT_LIST_SUCCESS:
      const contractsInPersons = {};
      action.payload?.contracts?.forEach(contract => {
        const arr = contractsInPersons[contract.mainTenantContactId] || [];

        contractsInPersons[contract.mainTenantContactId] = [contract.id, ...arr];
      });

      return {
        ...state,
        contracts: action.payload?.contracts,
        contractsInPersons: contractsInPersons,
        loadedEntities: [action.type, ...state.loadedEntities]
      };

    case Constants.GLOBAL_CONTRACT_CREATE_SUCCESS:
      return {
        ...state,
        contracts: state.contracts.some(contract => contract.id === action.payload?.contract?.id) ?
        [
          action.payload?.contract,
          ...state.contracts.filter(contract => contract.id !== action.payload?.contract?.id)
        ] : [action.payload?.contract, ...state.contracts],
        contractsInUnits: {
          ...state.contractsInUnits,
          [action.payload?.contract?.unit?.id]: [
            ...new Set([
              ...[action.payload?.contract?.id],
              ...state.contractsInUnits[action.payload?.contract?.unit?.id] || []
            ])
          ]
        },
        contractsInPersons: {
          ...state.contractsInPersons,
          [action.payload?.contract?.person?.id]: [
            ...new Set([
              ...[action.payload?.contract?.id],
              ...state.contractsInPersons[action.payload?.contract?.person?.id] || []
            ])
          ]
        }
      };

    case Constants.PAGE_CONTRACT_UPDATE_SUCCESS:
    case Constants.GLOBAL_CONTRACT_END_SUCCESS:
    {
      const contractsInUnits = {...state.contractsInUnits};
      let unitWasChanged = false;
      const contractsInPersons = {...state.contractsInPersons};
      let personWasChanged = false;

      Object.keys(contractsInUnits || {}).forEach(unit => {
        if (contractsInUnits[unit].includes(action.payload?.contract?.id) && unit !== action.payload?.contract?.unitId) {
          unitWasChanged = true;

          contractsInUnits[unit] = (contractsInUnits[unit] || []).filter(
            contractId => contractId !== action.payload?.contract?.id
          )
        }
      });

      if (unitWasChanged) {
        contractsInUnits[action.payload?.contract?.unitId] = [
          ...new Set([
            ...[action.payload?.contract?.id],
            ...state.contractsInUnits[action.payload?.contract?.unitId] || []
          ])
        ];
      }

      Object.keys(contractsInPersons || {}).forEach(person => {
        if (contractsInPersons[person].includes(action.payload?.contract?.id) && person !== action.payload?.contract?.mainTenantContactId) {
          personWasChanged = true;

          contractsInPersons[person] = (contractsInPersons[person] || []).filter(
            contractId => contractId !== action.payload?.contract?.id
          )
        }
      });

      if (personWasChanged) {
        contractsInPersons[action.payload?.contract?.mainTenantContactId] = [
          ...new Set([
            ...[action.payload?.contract?.id],
            ...state.contractsInPersons[action.payload?.contract?.mainTenantContactId] || []
          ])
        ];
      }

      return {
        ...state,
        contracts: [
          action.payload?.contract,
          ...state.contracts.filter(contract => contract.id !== action.payload?.contract.id)
        ],
        contractsInUnits: {...contractsInUnits},
        contractsInPersons: {...contractsInPersons},
      };
    }

    case Constants.GLOBAL_CONTRACT_DELETE_SUCCESS:
      return {
        ...state,
        contracts: [
          action.payload?.contract,
          ...state.contracts.filter(contract => contract.id !== action.payload?.contract?.id)
        ],
        contractsInUnits: {
          ...state.contractsInUnits,
          [action.payload?.contract?.unitId]: state.contractsInUnits[action.payload?.contract?.unitId].filter(
            id => id !== action.payload?.contract?.id
          )
        },
        contractsInPersons: {
          ...state.contractsInPersons,
          [action.payload?.contract?.mainTenantContactId]: state.contractsInPersons[action.payload?.contract?.mainTenantContactId].filter(
            id => id !== action.payload?.contract?.id
          )
        }
      };

    /*
    |  Charge Templates
    */

    case Constants.GLOBAL_CHARGE_TEMPLATE_LIST_SUCCESS:
      const chargeTemplatesInContracts = {};
      const chargeTemplatesInUnits = {};
      const chargeTemplatesInProperties = {};

      action.payload?.chargeTemplates?.forEach(chargeTemplate => {
        const current = chargeTemplatesInContracts[chargeTemplate.contractId] || [];
        chargeTemplatesInContracts[chargeTemplate.contractId] = [chargeTemplate.id, ...current];
      });

      action.payload?.chargeTemplates?.forEach(chargeTemplate => {
        const current = chargeTemplatesInUnits[chargeTemplate.unitId] || [];
        chargeTemplatesInUnits[chargeTemplate.unitId] = [chargeTemplate.id, ...current];
      });

      action.payload?.chargeTemplates?.forEach(chargeTemplate => {
        const current = chargeTemplatesInProperties[chargeTemplate.propertyId] || [];
        chargeTemplatesInProperties[chargeTemplate.propertyId] = [chargeTemplate.id, ...current];
      });

      return {
        ...state,
        chargeTemplates: action.payload?.chargeTemplates,
        chargeTemplatesInContracts: chargeTemplatesInContracts,
        chargeTemplatesInUnits: chargeTemplatesInUnits,
        chargeTemplatesInProperties: chargeTemplatesInProperties,
        loadedEntities: [action.type, ...state.loadedEntities]
      };

    case Constants.GLOBAL_CHARGE_TEMPLATE_CREATE_SUCCESS:
      return {
        ...state,

        chargeTemplates: state.chargeTemplates.some(chargeTemplate => chargeTemplate.id === action.payload?.chargeTemplate?.id) ?
        [
          action.payload?.chargeTemplate,
          ...state.chargeTemplates.filter(chargeTemplate => chargeTemplate.id !== action.payload?.chargeTemplate?.id)
        ] : [action.payload?.chargeTemplate, ...state.chargeTemplates],
        chargeTemplatesInContracts: {
          ...state.chargeTemplatesInContracts,
          [action.payload?.chargeTemplate?.contractId]: [
            ...new Set([
              ...[action.payload?.chargeTemplate?.id],
              ...state.chargeTemplatesInContracts[action.payload?.chargeTemplate?.contractId] || []
            ])
          ]
        },
        chargeTemplatesInUnits: {
          ...state.chargeTemplatesInUnits,
          [action.payload?.chargeTemplate?.unitId]: [
            ...new Set([
              ...[action.payload?.chargeTemplate?.id],
              ...state.chargeTemplatesInUnits[action.payload?.chargeTemplate?.unitId] || []
            ])
          ]
        },
        chargeTemplatesInProperties: {
          ...state.chargeTemplatesInProperties,
          [action.payload?.chargeTemplate?.propertyId]: [
            ...new Set([
              ...[action.payload?.chargeTemplate?.id],
              ...state.chargeTemplatesInProperties[action.payload?.chargeTemplate?.propertyId] || []
            ])
          ]
        }
      };

    case Constants.GLOBAL_CHARGE_TEMPLATE_UPDATE_SUCCESS:
      return {
        ...state,
        chargeTemplates: [
          action.payload?.chargeTemplate,
          ...state.chargeTemplates.filter(chargeTemplate => chargeTemplate.id !== action.payload?.chargeTemplate?.id)
        ],
        chargeTemplatesInContracts: {
          ...state.chargeTemplatesInContracts,
          [action.payload?.chargeTemplate?.contractId]: [
            ...new Set([
              action.payload?.chargeTemplate?.id,
              ...state.chargeTemplatesInContracts[action.payload?.chargeTemplate?.contractId] || []
            ])
          ]
        },
        chargeTemplatesInUnits: {
          ...state.chargeTemplatesInUnits,
          [action.payload?.chargeTemplate?.unitId]: [
            ...new Set([
              action.payload?.chargeTemplate?.id,
              ...state.chargeTemplatesInUnits[action.payload?.chargeTemplate?.unitId] || []
            ])
          ]
        },
        chargeTemplatesInProperties: {
          ...state.chargeTemplatesInProperties,
          [action.payload?.chargeTemplate?.propertyId]: [
            ...new Set([
              action.payload?.chargeTemplate?.id,
              ...state.chargeTemplatesInProperties[action.payload?.chargeTemplate?.propertyId] || []
            ])
          ]
        },
      };

    case Constants.GLOBAL_CHARGE_TEMPLATE_DELETE_SUCCESS:
      return {
        ...state,
        chargeTemplates: [
          action.payload?.chargeTemplate,
          ...state.chargeTemplates.filter(chargeTemplate => chargeTemplate.id !== action.payload?.chargeTemplate?.id)
        ],
        chargeTemplatesInContracts: {
          ...state.chargeTemplatesInContracts,
          [action.payload?.chargeTemplate?.contractId]: state.chargeTemplatesInContracts[action.payload?.chargeTemplate?.contractId]
          .filter(
            chargeTemplateId => chargeTemplateId !== action.payload?.chargeTemplate?.id
          )
        },
        chargeTemplatesInUnits: {
          ...state.chargeTemplatesInUnits,
          [action.payload?.chargeTemplate?.unitId]: state.chargeTemplatesInUnits[action.payload?.chargeTemplate?.unitId]
          .filter(
            chargeTemplateId => chargeTemplateId !== action.payload?.chargeTemplate?.id
          )
        },
        chargeTemplatesInProperties: {
          ...state.chargeTemplatesInProperties,
          [action.payload?.chargeTemplate?.propertyId]: state.chargeTemplatesInProperties[action.payload?.chargeTemplate?.propertyId]
          .filter(
            chargeTemplateId => chargeTemplateId !== action.payload?.chargeTemplate?.id
          )
        }
      };

    /*
    |  Agency Fees
    */

    case Constants.GLOBAL_AGENCY_FEE_LIST_SUCCESS:
      return {
        ...state,
        agencyFees: action.payload?.agencyFees,
        loadedEntities: [action.type, ...state.loadedEntities]
      };

    case Constants.GLOBAL_AGENCY_FEE_CREATE_SUCCESS:
      return {
        ...state,
        agencyFees: state.agencyFees.some(agencyFee => agencyFee.id === action.payload?.agencyFee?.id) ?
                      state.agencyFees : [action.payload?.agencyFee, ...state.agencyFees],
      };

    case Constants.GLOBAL_AGENCY_FEE_UPDATE_SUCCESS:
      return {
        ...state,
        agencyFees: [
          action.payload?.agencyFee,
          ...state.agencyFees.filter(agencyFee => agencyFee.id !== action.payload?.agencyFee?.id)
        ]
      };

    case Constants.GLOBAL_AGENCY_FEE_DELETE_SUCCESS:
      return {
        ...state,
        agencyFees: [
          action.payload?.agencyFee,
          ...state.agencyFees.filter(agencyFee => agencyFee.id !== action.payload?.agencyFee?.id)
        ]
      };

    case Constants.FINANCE_CHARGES_FILTER_UPDATE_STATE:
      return {
        ...state,
        financeChargesFilterState: action.payload?.filter
      };

    case Constants.FINANCE_CHARGES_FILTER_DATE_UPDATE_STATE:
      return {
        ...state,
        financeChargesFilterDateState: action.payload?.filter
      };

    case Constants.FINANCE_CHARGES_FILTER_SEARCH_UPDATE_STATE:
      return {
        ...state,
        financeChargesFilterSearchState: action.payload?.filter
      };

    case Constants.FINANCE_CHARGES_LIST_POSITION_SAVE:
      return {
        ...state,
        financeChargesListPosition: action.payload?.position
      };

    case Constants.DASHBOARD_STATEMENTS_LIST_POSITION_SAVE:
      return {
        ...state,
        dashboardStatementsListPosition: action.payload?.position
      };

    case Constants.DASHBOARD_CHARGES_LIST_POSITION_SAVE:
      return {
        ...state,
        dashboardChargesListPosition: action.payload?.position
      };

    case Constants.FINANCE_STATEMENTS_LIST_POSITION_SAVE:
      return {
        ...state,
        financeStatementsListPosition: action.payload?.position
      };

    /*
    |  Tasks
    */

    case Constants.TASK_FILTER_SEARCH_UPDATE_STATE:
      return {
        ...state,
        tasksFilterSearchState: action.payload?.filter
      };

    /*
    |  Notes
    */

    case Constants.NOTES_FILTER_UPDATE_STATE:
      return {
        ...state,
        notesFilterState: action.payload?.filter
      };

    case Constants.NOTES_FILTER_SEARCH_UPDATE_STATE:
      return {
        ...state,
        notesFilterSearchState: action.payload?.filter
      };

    /*
    |  Notifications
    */

    case Constants.NOTIFICATIONS_FILTER_UPDATE_STATE:
      return {
        ...state,
        notificationsFilterState: action.payload?.filter
      };

    case Constants.NOTIFICATIONS_FILTER_SEARCH_UPDATE_STATE:
      return {
        ...state,
        notificationsFilterSearchState: action.payload?.filter
      };

    /*
    |  Contracts
    */

    case Constants.CONTRACTS_FILTER_UPDATE_STATE:
      return {
        ...state,
        contractsFilterState: action.payload?.filter
      };

    case Constants.CONTRACTS_FILTER_SEARCH_UPDATE_STATE:
      return {
        ...state,
        contractsFilterSearchState: action.payload?.filter
      };
    /*
    |  Contacts
    */

    case Constants.CONTACTS_FILTER_UPDATE_STATE:
      return {
        ...state,
        contactsFilterState: action.payload?.filter
      };

    case Constants.CONTACTS_FILTER_SEARCH_UPDATE_STATE:
      return {
        ...state,
        contactsFilterSearchState: action.payload?.filter
      };
    /*
    |  Companies
    */

    case Constants.COMPANIES_FILTER_UPDATE_STATE:
      return {
        ...state,
        companiesFilterState: action.payload?.filter
      };

    case Constants.COMPANIES_FILTER_SEARCH_UPDATE_STATE:
      return {
        ...state,
        companiesFilterSearchState: action.payload?.filter
      };

    /*
    |  Activities
    */

    case Constants.ACTIVITIES_FILTER_UPDATE_STATE:
      return {
        ...state,
        activitiesFilterState: action.payload?.filter
      };

    case Constants.ACTIVITIES_FILTER_SEARCH_UPDATE_STATE:
      return {
        ...state,
        activitiesFilterSearchState: action.payload?.filter
      };


    /*
    |  Bank Transactions
    */

    case Constants.GLOBAL_TRANSACTION_FILE_LIST_SUCCESS:
      return {
        ...state,
        transactionFiles: action.payload?.transactionFiles,
        loadedEntities: [action.type, ...state.loadedEntities]
      };

    case Constants.GLOBAL_TRANSACTION_FILE_CREATE_SUCCESS:
      return {
        ...state,
        transactionFiles: [action.payload?.transactionFile, ...state.transactionFiles]
      };

    case Constants.GLOBAL_TRANSACTION_FILE_PAYMENT_SET_PROCESSED_SUCCESS:
    case Constants.GLOBAL_TRANSACTION_FILE_UPDATE_SUCCESS:
      return {
        ...state,
        transactionFiles: [
          action.payload?.transactionFile,
          ...state.transactionFiles.filter(transactionFile => transactionFile.id !== action.payload?.transactionFile?.id)
        ]
      };

    case Constants.GLOBAL_TRANSACTION_FILE_DELETE_SUCCESS:
      return {
        ...state,
        transactionFiles: [
          action.payload?.transactionFile,
          ...state.transactionFiles.filter(transactionFile => transactionFile.id !== action.payload?.transactionFile?.id)
        ]
      };

    /*
    |  Payments
    */

    case Constants.FINANCE_PAYMENTS_FILTER_UPDATE_STATE:
      return {
        ...state,
        financePaymentsFilterState: action.payload?.filter
      };

    case Constants.TRANSACTION_FILTER_UPDATE_STATE:
      const transactionsFilterStates = {...state.transactionsFilterStates};
      transactionsFilterStates[action.payload?.fileId] = transactionsFilterStates[action.payload?.fileId] || {};
      transactionsFilterStates[action.payload?.fileId]["filter"] = action.payload?.filter;
      transactionsFilterStates[action.payload?.fileId]["transactionIds"] = action.payload?.transactionIds;

      return {
        ...state,
        transactionsFilterStates: {...transactionsFilterStates}
      };

    case Constants.FINANCE_PAYMENTS_FILTER_DATE_UPDATE_STATE:
      return {
        ...state,
        financePaymentsFilterDateState: action.payload?.filter
      };

    case Constants.FINANCE_PAYMENTS_FILTER_BANK_ACCOUNT_UPDATE_STATE:
      return {
        ...state,
        financePaymentsFilterBankAccountState: action.payload?.index
      };

    case Constants.FINANCE_PAYMENTS_FILTER_SEARCH_UPDATE_STATE:
      return {
        ...state,
        financePaymentsFilterSearchState: action.payload?.filter
      };

    case Constants.SAVINGS_PAYMENTS_FILTER_SEARCH_UPDATE_STATE:
      const savingsPaymentsFilterSearchState = {...state.savingsPaymentsFilterSearchState};
      savingsPaymentsFilterSearchState[action.payload?.companyId] = action.payload?.filter;

      return {
        ...state,
        savingsPaymentsFilterSearchState: {...savingsPaymentsFilterSearchState}
      };

    case Constants.FINANCE_PAYMENTS_LIST_POSITION_SAVE:
      return {
        ...state,
        financePaymentsListPosition: action.payload?.position
      };

    case Constants.DASHBOARD_PAYMENTS_LIST_POSITION_SAVE:
      return {
        ...state,
        dashboardPaymentsListPosition: action.payload?.position
      };

    case Constants.SAVINGS_PAYMENTS_LIST_POSITION_SAVE:
      return {
        ...state,
        savingsPaymentsListPosition: action.payload?.position
      };

    /*
    |  Bank Transactions
    */

    case Constants.FINANCE_BANK_TRANSACTIONS_FILTER_UPDATE_STATE:
      return {
        ...state,
        financeBankTransactionsFilterState: action.payload?.filter
      };

    case Constants.FINANCE_BANK_TRANSACTIONS_LIST_POSITION_SAVE:
      return {
        ...state,
        financeBankTransactionsListPosition: action.payload?.position
      };


    /*
    |  Attachments Component
    */

    case Constants.GLOBAL_ATTACHMENT_UPLOAD_REQUEST:
      return {
        ...state,
        isUploadingPrivateAttachment: action.payload?.typeOfComponent === "private",
        isUploadingSharedAttachment: action.payload?.typeOfComponent === "shared"
      };

    case Constants.GLOBAL_ATTACHMENT_UPLOAD_SUCCESS:
      return {
        ...state,
        isUploadingPrivateAttachment: false,
        isUploadingSharedAttachment: false
      };

    case Constants.GLOBAL_ATTACHMENT_UPLOAD_FAILURE:
      return {
        ...state,
        isUploadingPrivateAttachment: false,
        isUploadingSharedAttachment: false
      };

    case Constants.GLOBAL_ATTACHMENT_PERSON_UPLOAD_SUCCESS:
      return {
        ...state,
        persons: [
          action.payload?.person,
          ...state.persons.filter(person => person.id !== action.payload?.person?.id)
        ],
        session: {
          ...state.session,
          user: action.payload?.person?.id === state.session.user.id ? action.payload?.person : state.session.user
        }
      };

    case Constants.GLOBAL_ATTACHMENT_PERSON_DELETE_SUCCESS:
      return {
        ...state,
        persons: [
          action.payload?.person,
          ...state.persons.filter(person => person.id !== action.payload?.person?.id)
        ],
        session: {
          ...state.session,
          user: action.payload?.person?.id === state.session.user.id ? action.payload?.person : state.session.user
        }
      };


    case Constants.GLOBAL_ATTACHMENT_COMPANY_UPLOAD_SUCCESS:
      return {
        ...state,
        companies: [
          action.payload?.company,
          ...state.companies.filter(company => company.id !== action.payload?.company?.id)
        ]
      };

    case Constants.GLOBAL_ATTACHMENT_COMPANY_DELETE_SUCCESS:
      return {
        ...state,
        companies: [
          action.payload?.company,
          ...state.companies.filter(company => company.id !== action.payload?.company?.id)
        ]
      };

    case Constants.GLOBAL_ATTACHMENT_CONTRACT_UPLOAD_SUCCESS:
      return {
        ...state,
        contracts: [
          action.payload?.contract,
          ...state.contracts.filter(contract => contract.id !== action.payload?.contract?.id)
        ]
      };

    case Constants.GLOBAL_ATTACHMENT_CONTRACT_DELETE_SUCCESS:
      return {
        ...state,
        contracts: [
          action.payload?.contract,
          ...state.contracts.filter(contract => contract.id !== action.payload?.contract?.id)
        ]
      };

    /*
    |  Avatar
    */

    case Constants.GLOBAL_SET_AVATAR_SUCCESS:
      switch (action.payload?.entity?.nameOfEntity?.toLowerCase()) {
        case Translator.trans("person").toLowerCase():
          return {
            ...state,
            persons: [
              action.payload?.entity,
              ...state.persons.filter(entity => entity.id !== action.payload?.entity?.id)
            ],
            session: {
              ...state.session,
              user: action.payload?.entity?.id === state.session.user.id ? action.payload?.entity : state.session.user
            }
          };

        case Translator.trans("company").toLowerCase():
          return {
            ...state,
            companies: [
              action.payload?.entity,
              ...state.companies.filter(entity => entity.id !== action.payload?.entity?.id)
            ]
          };

        case Translator.trans("contract").toLowerCase():
          return {
            ...state,
            contracts: [
              action.payload?.entity,
              ...state.contracts.filter(entity => entity.id !== action.payload?.entity?.id)
            ]
          };

        default:
          return {
            ...state
          };
      }

    /*
    |  Email Accounts
    */

    case Constants.GLOBAL_EMAIL_ACCOUNT_LIST_SUCCESS:
      return {
        ...state,
        emailAccounts: action.payload?.emailAccounts,
        loadedEntities: [action.type, ...state.loadedEntities]
      };

    case Constants.PAGE_SETTINGS_EMAIL_ACCOUNT_CREATE_SUCCESS:
      return {
        ...state,
        emailAccounts: [action.payload?.emailAccount, ...state.emailAccounts]
      };

    case Constants.PAGE_SETTINGS_EMAIL_ACCOUNT_UPDATE_SUCCESS:
      return {
        ...state,
        emailAccounts: [
          action.payload?.emailAccount,
          ...state.emailAccounts.filter(emailAccount => emailAccount.id !== action.payload?.emailAccount?.id)
        ]
      };

    case Constants.PAGE_SETTINGS_EMAIL_ACCOUNT_DELETE_SUCCESS:
      return {
        ...state,
        emailAccounts: [
          action.payload?.emailAccount,
          ...state.emailAccounts.filter(emailAccount => emailAccount.id !== action.payload?.emailAccount?.id)
        ]
      };

    /*
    |  Subscriptions
    */

    case Constants.GLOBAL_SUBSCRIPTION_LIST_SUCCESS:
      const subscriptions = {};
      action.payload?.subscriptions?.forEach(subscription => {
        if (typeof subscriptions[subscription.subscriptionType.id] === "undefined") {
          subscriptions[subscription.subscriptionType.id] = [];
        }

        subscriptions[subscription.subscriptionType.id] = [
          subscription,
          ...subscriptions[subscription.subscriptionType.id]
        ];
      });

      return {
        ...state,
        subscriptions: subscriptions,
        loadedEntities: [action.type, ...state.loadedEntities]
      };

    case Constants.GLOBAL_SUBSCRIPTION_CREATE_SUCCESS:
      return {
        ...state,
        subscriptions: {
          ...state.subscriptions,
          [action.payload?.subscription?.subscriptionType?.id]: [
            ...new Set([
              ...[action.payload?.subscription],
              ...state.subscriptions[action.payload?.subscription?.subscriptionType?.id] || []
            ])
          ]
        }
      };

    case Constants.GLOBAL_SUBSCRIPTION_DELETE_SUCCESS:
      return {
        ...state,
        subscriptions: {
          ...state.subscriptions,
          [action.payload?.subscription?.subscriptionTypeId]: state.subscriptions[action.payload?.subscription?.subscriptionTypeId]
          .filter(
            subscription => subscription.id !== action.payload?.subscription?.id
          )
        }
      };

    /*
    |  Socket
    */

    case Constants.SOCKET_CONNECTED:
      return {
        ...state,
        socketDisconnected: false
      };

    case Constants.SOCKET_DISCONNECTED:
      return {
        ...state,
        socketDisconnected: true
      };

    /*
    |  Table Component
    */

    case Constants.TABLE_COMPONENT_PAGINATOR_INCREASE:
      return {
        ...state,
        paginator: {
          ...state.paginator,
          [action.payload?.name]: (state.paginator[action.payload?.name] || 0) + action.payload?.amount
        }
      };

    /*
    |  Saving data before redirecting to create page
    */
    case Constants.SAVED_DATA_BEFORE_REDIRECTING:
      return {
        ...state,
        savedDataBeforeRedirecting: action.payload?.savedDataBeforeRedirecting
      };

    case Constants.SAVED_DATA_BEFORE_REDIRECTING_CLEAR:
      return {
        ...state,
        savedDataBeforeRedirecting: {}
      };

    case Constants.PROPERTY_SAVED_DATA_BEFORE_REDIRECTING_CLEAR:
      {
        const savedDataBeforeRedirecting: any = {...state.savedDataBeforeRedirecting};
        delete savedDataBeforeRedirecting.propertyCreateDataToBeSaved;

        return {
          ...state,
          savedDataBeforeRedirecting: {...savedDataBeforeRedirecting}
        };
      }

    case Constants.UNIT_SAVED_DATA_BEFORE_REDIRECTING_CLEAR:
      {
        const savedDataBeforeRedirecting: any = {...state.savedDataBeforeRedirecting};
        delete savedDataBeforeRedirecting.unitCreateDataToBeSaved;

        return {
          ...state,
          savedDataBeforeRedirecting: {...savedDataBeforeRedirecting}
        };
      }

    /*
    |  Breadcrumbs
    */
    case Constants.UPDATE_BREADCRUMBS:
      return {
        ...state,
        breadcrumbs: action.payload?.breadcrumbs
      };

    case Constants.BREADCRUMBS_CLEAR:
      return {
        ...state,
        breadcrumbs: []
      };

    /*
    |  Person create page template
    */
    case Constants.PERSON_CREATE_PAGE_TEMPLATE:
      return {
        ...state,
        templateForPersonCreatePage: action.payload?.template
      };

    case Constants.PERSON_CREATE_PAGE_TEMPLATE_CLEAR:
      return {
        ...state,
        templateForPersonCreatePage: ""
      };

    /*
    |  Company create page template
    */
    case Constants.COMPANY_CREATE_PAGE_TEMPLATE:
      return {
        ...state,
        templateForCompanyCreatePage: action.payload?.template
      };

    case Constants.COMPANY_CREATE_PAGE_TEMPLATE_CLEAR:
      return {
        ...state,
        templateForCompanyCreatePage: ""
      };

    /*
    |  Redirect path
    */
    case Constants.REDIRECT_PATH:
      return {
        ...state,
        redirectPath: action.payload?.redirectPath
      };

    case Constants.REDIRECT_PATH_CLEAR:
      return {
        ...state,
        redirectPath: ""
      };

    /*
    |  Redirect path
    */
    case Constants.POST_PROCESS_MESSAGE:
      return {
        ...state,
        postProcessMessage: action.payload?.postProcessMessage
      };

    case Constants.POST_PROCESS_MESSAGE_CLEAR:
      return {
        ...state,
        postProcessMessage: ""
      };

    case Constants.POST_PROCESS_SIGN_UP_MESSAGE:
      return {
        ...state,
        postProcessSignUpPopupOpenMessage: action.payload?.postProcessSignUpPopupOpenMessage
      };

    case Constants.POST_PROCESS_SIGN_UP_MESSAGE_CLEAR:
      return {
        ...state,
        postProcessSignUpPopupOpenMessage: ""
      };

    /*
    |  Activiteit sjablonen
    */
    case Constants.GLOBAL_ACTIVITY_TEMPLATE_LIST_SUCCESS:
      return {
        ...state,
        activityTemplates: action.payload?.activityTemplates.filter(activityTemplate => !activityTemplate.removed),
        loadedEntities: [action.type, ...state.loadedEntities]
      };

    case Constants.GLOBAL_ACTIVITY_TEMPLATE_CREATE_SUCCESS:
      return {
        ...state,
        activityTemplates: state.activityTemplates.some(activityTemplate => activityTemplate.id === action.payload?.activityTemplate?.id) ?
          state.activityTemplates : [action.payload?.activityTemplate, ...state.activityTemplates]
      };

    case Constants.GLOBAL_ACTIVITY_TEMPLATE_UPDATE_SUCCESS:
      return {
        ...state,
        activityTemplates: [
          action.payload?.activityTemplate,
          ...state.activityTemplates.filter(activityTemplate => activityTemplate.id !== action.payload?.activityTemplate?.id)
        ]
      };

    case Constants.GLOBAL_ACTIVITY_TEMPLATE_DELETE_SUCCESS:
      return {
        ...state,
        activityTemplates: [
          action.payload?.activityTemplate,
          ...state.activityTemplates.filter(activityTemplate => activityTemplate.id !== action.payload?.activityTemplate?.id)
        ]
      };

    /*
    |  Task Templates
    */
    case Constants.GLOBAL_TASK_TEMPLATE_LIST_SUCCESS:
      const taskTemplatesInActivityTemplates = {};
      action.payload?.taskTemplates?.forEach(taskTemplate => {
        const current = taskTemplatesInActivityTemplates[taskTemplate.activityTemplateId] || [];
        taskTemplatesInActivityTemplates[taskTemplate.activityTemplateId] = [taskTemplate.id, ...current];
      });

      return {
        ...state,
        taskTemplates: action.payload?.taskTemplates,
        taskTemplatesInActivityTemplates: taskTemplatesInActivityTemplates,
        loadedEntities: [action.type, ...state.loadedEntities]
      };

    case Constants.GLOBAL_TASK_TEMPLATE_CREATE_SUCCESS:
      return {
        ...state,
        taskTemplates: state.taskTemplates.some(taskTemplate => taskTemplate.id === action.payload?.taskTemplate?.id) ?
          state.taskTemplates : [action.payload?.taskTemplate, ...state.taskTemplates],
          taskTemplatesInActivityTemplates: {
          ...state.taskTemplatesInActivityTemplates,
          [action.payload?.taskTemplate?.activityTemplateId]: [
            ...new Set([
              ...[action.payload?.taskTemplate?.id],
              ...state.taskTemplatesInActivityTemplates[action.payload?.taskTemplate?.activityTemplateId] || []
            ])
          ]
        }
      };

    case Constants.GLOBAL_TASK_TEMPLATE_UPDATE_SUCCESS:
    case Constants.GLOBAL_TASK_TEMPLATE_DELETE_SUCCESS:
    {
      const taskTemplatesInActivityTemplates = {...state.taskTemplatesInActivityTemplates};

      if (action.payload?.taskTemplate?.removed) {
        taskTemplatesInActivityTemplates[action.payload?.taskTemplate?.activityTemplateId] = (taskTemplatesInActivityTemplates[action.payload?.taskTemplate?.activityTemplateId] || []).filter(
          templateId => templateId !== action.payload?.taskTemplate?.id
        )
      }

      return {
        ...state,
        taskTemplates: [
          action.payload?.taskTemplate,
          ...state.taskTemplates.filter(taskTemplate => taskTemplate.id !== action.payload?.taskTemplate?.id)
        ],
        taskTemplatesInActivityTemplates: {...taskTemplatesInActivityTemplates}
      };
    }

  /*
  |  Bank Accounts
  */
  case Constants.GLOBAL_BANK_ACCOUNT_LIST_SUCCESS:
    return {
      ...state,
      bankAccounts: action.payload?.bankAccounts,
      loadedEntities: [action.type, ...state.loadedEntities]
    };

  case Constants.GLOBAL_BANK_ACCOUNT_CREATE_SUCCESS:
    return {
      ...state,
      bankAccounts: state.bankAccounts.some(bankAccount => bankAccount.id === action.payload?.bankAccount?.id) ?
        state.bankAccounts : [action.payload?.bankAccount, ...state.bankAccounts]
    };

  case Constants.GLOBAL_BANK_ACCOUNT_UPDATE_SUCCESS:
    return {
      ...state,
      bankAccounts: [
        action.payload?.bankAccount,
        ...state.bankAccounts.filter(bankAccount => bankAccount.id !== action.payload?.bankAccount?.id)
      ]
    };

  case Constants.GLOBAL_BANK_ACCOUNT_DELETE_SUCCESS:
    return {
      ...state,
      bankAccounts: [
        action.payload?.bankAccount,
        ...state.bankAccounts.filter(bankAccount => bankAccount.id !== action.payload?.bankAccount?.id)
      ]
    };

  case Constants.GLOBAL_LAUNCH_PANEL_STATE:
    return {
      ...state,
      launchPanelHide: action.payload?.launchPanelState
    };

  case Constants.PONTO_CONNECTION_MESSAGE_SHOWN:
    return {
      ...state,
      pontoMessageShown: action?.payload
    };

  case Constants.PONTO_SYNCHRONIZATION_BUTTON_DISABLED:
    return {
      ...state,
      pontoSynchronizeButtonDisabled: action?.payload
    };

  case Constants.ACTIVATE_CLIENT_ACCOUNT:
    return {
      ...state,
      clientAccount: action?.payload
    };

    default:
      return state;
  }
};
